import { safeJson } from '@/scripts/utils';
import type { FileInfo } from '@/contexts/chat/scripts/hooks/useFileMessages/types';
import { ChatMessagePayloadType } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { FileTypeIcon } from '@/configs/options/file';
import type { ReplyMessage, SetReplyMessage } from '@/scripts/hooks/useReply';
import { isMeMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/utils';
import { useSessionContext } from '@/contexts/session/context';
import type { ChatContextHooksFull } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import useMessageExpireTimer from '@/scripts/hooks/useMessageExpireTimer';
import ReplyIcon from '../../images/reply.svg?react';
import CloseIcon from '../../images/close.svg?react';
import { deserializePayloadContent } from '../../helpers';
import classes from './index.module.scss';

export const Reply = ({
  chat,
  chatGroup,
  replyMessage,
  setReplyMessage,
}: {
  chat: Chat;
  chatGroup: ChatContextHooksFull['chatGroup'];
  replyMessage: ReplyMessage;
  setReplyMessage?: SetReplyMessage;
}) => {
  const {
    session: { user },
  } = useSessionContext();

  const [replyMsgName, setReplyMsgName] = useState<string | undefined>();

  const refs = useRefs({
    chat,
    getMembersByUserId: chatGroup?.getMembersByUserId,
  });

  const message = useMemo(() => {
    return replyMessage?.message;
  }, [replyMessage?.message]);

  const info = useMemo(
    () => safeJson(message?.payload || '', {} as FileInfo),
    [message?.payload]
  );

  const isFile = useMemo(() => {
    return (
      message?.payload_type === ChatMessagePayloadType.FILE ||
      message?.payload_type === ChatMessagePayloadType.IMAGE
    );
  }, [message?.payload_type]);

  const replyInfo = useMemo(() => {
    switch (message?.payload_type) {
      case ChatMessagePayloadType.FILE:
        return `[File] ${info.name}`;
      case ChatMessagePayloadType.IMAGE:
        return 'Photo';
      default:
        return deserializePayloadContent(message?.payload ?? '');
    }
  }, [info.name, message?.payload, message?.payload_type]);

  useMessageExpireTimer({
    message: message!,
    chat,
    onExpire: () => {
      closeReply();
    },
  });

  const getReplyMsgName = useCallback(async () => {
    if (!message) return '';
    const { chat, getMembersByUserId } = refs.current;
    if (chat.category === ChatCategory.SINGLE) {
      const isMe = isMeMessage(message);
      setReplyMsgName(isMe ? user?.name : chat.name);
      return;
    }
    if (getMembersByUserId) {
      const [
        {
          args: [member],
        },
      ] = await getMembersByUserId([{ id: chat.chat_id, args: [message?.owner_id] }]);
      setReplyMsgName(member?.name);
    }
  }, [message, refs, user?.name]);

  useEffect(() => {
    getReplyMsgName();
  }, [getReplyMsgName]);

  const closeReply = useCallback(() => {
    setReplyMessage?.(null);
  }, [setReplyMessage]);

  return (
    <div
      className={classes.reply}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className={classes.replyIcon}>
        <ReplyIcon />
      </div>
      <div className={classes.replyDivider}></div>
      <div className={classes.replyContent}>
        {isFile && (
          <FileTypeIcon
            width={28}
            height={28}
            style={{ marginBottom: 6, borderRadius: 4 }}
            extType={info.ext}
            src={info.thumbnail}
          />
        )}
        <div className={classes.replyBody}>
          <div className={classes.replyTitle}>Reply to {replyMsgName}</div>
          <div className={classes.replyInfo}>{replyInfo}</div>
        </div>
        <CloseIcon className="pointer" onClick={closeReply} />
      </div>
    </div>
  );
};
